import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  LabelList,
} from "recharts";
import { Box, Typography } from "@mui/material";
import { BarChart as BarChartIcon } from "@mui/icons-material";
import { COLORS } from "../../../styles/uiConstants";
import EmptyState from "../../atoms/EmptyState";
import { FLOW_COLORS, formatMonthTR, formatToken } from "./tokenChartTheme";

/**
 * X ekseni etiketi: ay adı + o ayın NET değişimi.
 *
 * Net'i ikinci bir eksene (çizgi) veya grafiğin altına elle hizalanmış bir
 * satıra koymak yerine tick'in içine yazıyoruz — recharts tick'i kategori
 * bandının tam ortasına koyduğu için hizalama kendiliğinden doğru olur ve
 * ikinci y-ekseni ihtiyacı doğmaz.
 */
function MonthTick({ x, y, payload, rows = [] }) {
  const row = rows[payload?.index] || {};
  const net = row.net || 0;
  const netColor = net === 0 ? COLORS.slate400 : net > 0 ? FLOW_COLORS.in : FLOW_COLORS.out;

  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" dy={14} style={{ fontSize: 12, fontWeight: 600, fill: COLORS.slate600 }}>
        {payload?.value}
      </text>
      <text textAnchor="middle" dy={30} style={{ fontSize: 11, fontWeight: 700, fill: netColor }}>
        {`net ${net > 0 ? "+" : ""}${formatToken(net)}`}
      </text>
    </g>
  );
}

/**
 * Aylık token hareketi — ıraksayan (diverging) sütun grafiği.
 *
 * Yükleme sıfırın ÜSTÜNDE, tüketim ALTINDA çizilir; net değişim doğrudan
 * sütunun yanına yazılır. Eskiden giriş ve çıkış tipleri altı renkle TEK bir
 * yığın sütunda toplanıyordu — matematiksel olarak anlamsız bir toplam
 * (giriş + çıkış) üretiyor ve renk sayısı okumayı zorlaştırıyordu.
 *
 * @param {Object[]} data `{ month, inflow, outflow, net }` — inflow/outflow POZİTİF
 */
export default function MonthlyFlowChart({ data = [], height = 300 }) {
  const fill = height === "fill";
  const rows = useMemo(
    () =>
      data.map((d) => ({
        month: d.month,
        label: formatMonthTR(d.month),
        // Çıkış negatif eksende çizilir; tooltip'te mutlak değer gösterilir.
        Yükleme: Math.abs(d.inflow || 0),
        Tüketim: -Math.abs(d.outflow || 0),
        net: d.net || 0,
      })),
    [data]
  );

  const hasAnyValue = rows.some((r) => r.Yükleme !== 0 || r.Tüketim !== 0);

  if (rows.length === 0 || !hasAnyValue) {
    return (
      <EmptyState
        variant="compact"
        icon={<BarChartIcon />}
        title="Bu dönemde token hareketi yok"
        sx={{ height: fill ? "100%" : height, py: 0 }}
      />
    );
  }

  return (
    // height="fill": kapsayıcının kalan yüksekliğini doldurur (ekrana sığan panolar)
    <Box sx={fill ? { display: "flex", flexDirection: "column", height: "100%", minHeight: 0 } : undefined}>
      {/* Lejant — kimlik hiçbir zaman yalnız renkle taşınmaz */}
      <Box sx={{ display: "flex", gap: 2.5, mb: 1.5, flexWrap: "wrap" }}>
        {[
          { color: FLOW_COLORS.in, label: "Yükleme", hint: "fatura, ücretsiz, geri alma" },
          { color: FLOW_COLORS.out, label: "Tüketim", hint: "kullanım ve iade" },
        ].map((item) => (
          <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: item.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: COLORS.slate700 }}>
              {item.label}
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", color: COLORS.slate400 }}>
              ({item.hint})
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={fill ? { flex: 1, minHeight: 0 } : undefined}>
      <ResponsiveContainer width="100%" height={fill ? "100%" : height}>
        <BarChart data={rows} margin={{ top: 20, right: 12, left: 0, bottom: 4 }} barGap={4}>
          <CartesianGrid stroke={COLORS.slate100} vertical={false} />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            height={46}
            interval={0}
            tick={<MonthTick rows={rows} />}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            width={56}
            tick={{ fontSize: 11, fill: COLORS.slate400 }}
            tickFormatter={(v) => formatToken(Math.abs(v))}
          />
          <ReferenceLine y={0} stroke={COLORS.slate300} strokeWidth={1} />
          <Tooltip
            cursor={{ fill: COLORS.slate50 }}
            contentStyle={{
              borderRadius: 8,
              border: `1px solid ${COLORS.slate200}`,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              fontSize: 12,
            }}
            formatter={(value, name) => [`${formatToken(Math.abs(value))} token`, name]}
            labelFormatter={(label) => label}
          />
          <Bar dataKey="Yükleme" fill={FLOW_COLORS.in} radius={[4, 4, 0, 0]} maxBarSize={44}>
            <LabelList
              dataKey="Yükleme"
              position="top"
              formatter={(v) => (v ? formatToken(v) : "")}
              style={{ fontSize: 11, fontWeight: 700, fill: COLORS.slate600 }}
            />
          </Bar>
          <Bar dataKey="Tüketim" fill={FLOW_COLORS.out} radius={[0, 0, 4, 4]} maxBarSize={44}>
            <LabelList
              dataKey="Tüketim"
              position="bottom"
              formatter={(v) => (v ? formatToken(Math.abs(v)) : "")}
              style={{ fontSize: 11, fontWeight: 700, fill: COLORS.slate600 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      </Box>
    </Box>
  );
}
